// Google Analytics utility functions for event tracking
// Ensures gtag is available before attempting to track events

// Environment configuration
const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';
const GA_MEASUREMENT_ID = 'G-2RSNPLS29J';

// Only enable analytics in production unless explicitly enabled in development
const isAnalyticsEnabled = isProduction || process.env.REACT_APP_ENABLE_GA_DEV === 'true';

/**
 * Initialize Google Analytics with proper consent management
 * Call this once on app initialization
 */
export const initializeAnalytics = () => {
  if (!isAnalyticsEnabled || typeof window === 'undefined') {
    if (isDevelopment) {
      console.log('📊 GA Initialization Skipped (disabled)');
    }
    return;
  }

  // Set up consent defaults if not already done
  if (window.gtag && !window.gaInitialized) {
    // Check existing consent
    const existingConsent = localStorage.getItem('cookieConsent');
    
    if (existingConsent === 'accepted') {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }
    
    window.gaInitialized = true;
    
    if (isDevelopment) {
      console.log('📊 GA Initialized with consent:', existingConsent || 'pending');
    }
  }
};

/**
 * Track a custom Google Analytics event
 * @param {string} action - The action being tracked (e.g., 'click', 'submit')
 * @param {string} category - Event category (e.g., 'engagement', 'booking')
 * @param {string} label - Optional event label for additional context
 * @param {number} value - Optional numeric value associated with the event
 */
export const trackEvent = (action, category, label = null, value = null) => {
  // Only track if analytics is enabled and gtag is available
  if (!isAnalyticsEnabled || typeof window === 'undefined' || !window.gtag) {
    if (isDevelopment) {
      console.log('📊 GA Event Skipped (disabled):', { action, category, label, value });
    }
    return;
  }

  // Check if user has consented to analytics
  const hasConsent = localStorage.getItem('cookieConsent') === 'accepted';
  if (!hasConsent) {
    if (isDevelopment) {
      console.log('📊 GA Event Skipped (no consent):', { action, category, label, value });
    }
    return;
  }

  // Track the event
  if (window.gtag) {
    const eventData = {
      event_category: category,
      event_label: label,
      value: value
    };

    // Remove null values
    Object.keys(eventData).forEach(key => 
      eventData[key] === null && delete eventData[key]
    );

    window.gtag('event', action, eventData);
    
    // Log for debugging in development
    if (isDevelopment) {
      console.log('📊 GA Event Tracked:', { action, category, label, value });
    }
  }
};

/**
 * Track page views in Single Page Applications
 * @param {string} pagePath - The page path (e.g., '/booking', '/dashboard')
 * @param {string} pageTitle - Optional page title
 */
export const trackPageView = (pagePath, pageTitle = null) => {
  // Only track if analytics is enabled
  if (!isAnalyticsEnabled || typeof window === 'undefined' || !window.gtag) {
    if (isDevelopment) {
      console.log('📊 GA Page View Skipped (disabled):', { pagePath, pageTitle });
    }
    return;
  }

  // Check if user has consented to analytics
  const hasConsent = localStorage.getItem('cookieConsent') === 'accepted';
  if (!hasConsent) {
    if (isDevelopment) {
      console.log('📊 GA Page View Skipped (no consent):', { pagePath, pageTitle });
    }
    return;
  }

  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: pagePath,
    page_title: pageTitle
  });
  
  if (isDevelopment) {
    console.log('📊 GA Page View Tracked:', { pagePath, pageTitle });
  }
};

/**
 * Track booking-related events
 */
export const trackBookingEvent = (step, value = null) => {
  trackEvent('booking_step', 'booking', step, value);
};

/**
 * Track button clicks
 */
export const trackButtonClick = (buttonName, location = null) => {
  trackEvent('click', 'engagement', `${buttonName}${location ? `_${location}` : ''}`, null);
};

/**
 * Track form submissions
 */
export const trackFormSubmission = (formName, success = true) => {
  trackEvent('form_submit', 'engagement', `${formName}_${success ? 'success' : 'error'}`, null);
};

/**
 * Track user registration
 */
export const trackUserRegistration = (method = 'email') => {
  trackEvent('sign_up', 'user', method, null);
};

/**
 * Track user login
 */
export const trackUserLogin = (method = 'email') => {
  trackEvent('login', 'user', method, null);
};

/**
 * Track conversions (completed bookings)
 */
export const trackConversion = (bookingValue, currency = 'USD') => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: Date.now().toString(),
      value: bookingValue,
      currency: currency,
      event_category: 'conversion'
    });
  }
};